// src/pages/ZielePage.tsx
import React from 'react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore'; 
import { Page, Section, PageTitle } from '../components/UI';
import { Lock } from 'lucide-react';

interface Ziel {
    id: string;
    text: React.ReactNode;
}

interface StatusOption {
    value: string;
    label: string;
    color: string;
}

const DashboardStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

        .text-phantom-gold { color: #E6C299; }

        /* Ruhiger, edler Seitenhintergrund */
        .bg-executive-night {
            background:
                radial-gradient(circle at top, rgba(18,28,44,0.38) 0%, rgba(7,9,13,0) 36%),
                linear-gradient(to bottom, #07090d 0%, #050608 100%);
            background-color: #050608;
        }

        /* Carbon / Ackerbohnen Pattern – diesmal nur subtil eingesetzt */
        .carbon-weave-bg {
            background-color: #111;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        /* Große ruhige Außenfläche */
        .executive-surface {
            position: relative;
            border-radius: 1.9rem;
            border: 1px solid #182230;
            background:
                linear-gradient(to bottom, rgba(13,18,25,0.98), rgba(8,10,14,0.99));
            box-shadow:
                0 32px 90px rgba(0,0,0,0.60),
                inset 0 1px 0 rgba(255,255,255,0.04),
                inset 0 -1px 0 rgba(255,255,255,0.02);
        }

        .executive-surface::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            background:
                linear-gradient(to bottom, rgba(255,255,255,0.02), transparent 16%, rgba(0,0,0,0.20) 100%);
            pointer-events: none;
        }

        /* Hauptmodul für Tabelle / Ziele – mehr Tiefe + etwas Carbon */
        .ziele-module {
            position: relative;
            border-radius: 1.3rem;
            border: 1px solid #263242;
            background:
                linear-gradient(to bottom, rgba(8,14,24,0.96), rgba(4,8,14,0.97));
            box-shadow:
                0 22px 48px rgba(0,0,0,0.42),
                inset 0 1px 0 rgba(255,255,255,0.04),
                inset 0 -10px 35px rgba(0,0,0,0.25);
            overflow: hidden;
        }

        .ziele-module::before {
            content: '';
            position: absolute;
            inset: 0;
            opacity: 0.20;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent);
            background-size: 20px 20px;
            pointer-events: none;
        }

        .ziele-module::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            background:
                linear-gradient(to bottom, rgba(255,255,255,0.03), transparent 18%, rgba(0,0,0,0.16) 100%);
            pointer-events: none;
        }

        .content-layer {
            position: relative;
            z-index: 10;
        }

        /* Header-Zeile */
        .ziele-header-row {
            background:
                linear-gradient(to bottom, rgba(255,255,255,0.015), rgba(0,0,0,0.10));
            box-shadow: inset 0 -1px 0 rgba(255,255,255,0.03);
        }

        /* Einzelne Datenzeilen – leicht plastischer */
        .ziele-row {
            background:
                linear-gradient(to bottom, rgba(255,255,255,0.01), rgba(0,0,0,0.08));
        }

        .ziele-row:hover {
            background:
                linear-gradient(to bottom, rgba(230,194,153,0.035), rgba(0,0,0,0.08));
        }

        .section-divider {
            border-color: #2a3545;
        }

        html,
        body,
        #root,
        #__next,
        main {
            background: #050608 !important;
            background-color: #050608 !important;
        }

        .ziele-carbon-shell {
            background: #050608 !important;
            background-color: #050608 !important;
            min-height: 100vh;
        }

        body:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) #root,
        body:has(.ziele-carbon-shell) #__next,
        body:has(.ziele-carbon-shell) main,
        body:has(.ziele-carbon-shell) div:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) section:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="page"]:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="Page"]:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="layout"]:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="Layout"]:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="wrapper"]:has(.ziele-carbon-shell),
        body:has(.ziele-carbon-shell) [class*="Wrapper"]:has(.ziele-carbon-shell) {
            background: #050608 !important;
            background-color: #050608 !important;
            background-image: none !important;
        }

        /* Nur unerwünschte Wrapper-Hintergründe neutralisieren – keine Statusfarben kaputt machen */
        .ziele-carbon-shell > *,
        .ziele-carbon-shell section,
        .ziele-carbon-shell section > div,
        .ziele-carbon-shell [class*="bg-slate-900"],
        .ziele-carbon-shell [class*="bg-slate-950"],
        .ziele-carbon-shell [class*="bg-gray-900"],
        .ziele-carbon-shell [class*="bg-gray-950"],
        .ziele-carbon-shell [class*="bg-zinc-900"],
        .ziele-carbon-shell [class*="bg-zinc-950"],
        .ziele-carbon-shell [class*="bg-neutral-900"],
        .ziele-carbon-shell [class*="bg-neutral-950"] {
            background-image: none !important;
        }

        .ziele-select {
            background-image: none;
        }

        .ziele-select option {
            background: #111;
            color: #fff;
        }

        /* ============================================================
           LIGHT-MODE OVERRIDES (Ring 4c) — Dark Mode bleibt pixelidentisch,
           da alle Regeln nur unter html:not(.dark) greifen.
           ============================================================ */
        html:not(.dark),
        html:not(.dark) body,
        html:not(.dark) #root,
        html:not(.dark) #__next,
        html:not(.dark) main {
            background: #F1EFEA !important;
            background-color: #F1EFEA !important;
        }
        html:not(.dark) .ziele-carbon-shell {
            background: #F1EFEA !important;
            background-color: #F1EFEA !important;
        }
        html:not(.dark) body:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) #root,
        html:not(.dark) body:has(.ziele-carbon-shell) #__next,
        html:not(.dark) body:has(.ziele-carbon-shell) main,
        html:not(.dark) body:has(.ziele-carbon-shell) div:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) section:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="page"]:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="Page"]:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="layout"]:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="Layout"]:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="wrapper"]:has(.ziele-carbon-shell),
        html:not(.dark) body:has(.ziele-carbon-shell) [class*="Wrapper"]:has(.ziele-carbon-shell) {
            background: #F1EFEA !important;
            background-color: #F1EFEA !important;
        }
        html:not(.dark) .text-phantom-gold { color: #8D6435; }
        html:not(.dark) .bg-executive-night {
            background: #F1EFEA;
            background-color: #F1EFEA;
        }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .executive-surface {
            border: 1px solid #D6D3D1;
            background: #FFFFFF;
            box-shadow: 0 12px 34px -14px rgba(28,25,23,0.18);
        }
        html:not(.dark) .executive-surface::after { background: none; }
        html:not(.dark) .ziele-module {
            border: 1px solid #D6D3D1;
            background: #FBFAF8;
            box-shadow: 0 8px 22px -12px rgba(28,25,23,0.14);
        }
        html:not(.dark) .ziele-module::before { opacity: 0; }
        html:not(.dark) .ziele-module::after { background: none; }
        html:not(.dark) .ziele-header-row {
            background: #EAE7E2;
            box-shadow: inset 0 -1px 0 rgba(28,25,23,0.06);
        }
        html:not(.dark) .ziele-row { background: none; }
        html:not(.dark) .ziele-row:hover { background: rgba(141,100,53,0.06); }
        html:not(.dark) .section-divider { border-color: #D6D3D1; }
        html:not(.dark) .ziele-select option {
            background: #FFFFFF;
            color: #1C1917;
        }
    `}</style>
);

const ZielePage: React.FC = () => {
    const { clientData, setClientData } = useAppStore(state => ({
        clientData: state.clientData,
        setClientData: state.setClientData,
    }));
    
    // 💉 GOD-MODE CHECK: Wenn impersonatedClientId gefüllt ist, ist der Coach eingeloggt.
    // Wenn es leer/null ist, ist der Mandant selbst eingeloggt und bekommt nur Read-Only.
    const { impersonatedClientId } = useAdminStore();
    const isReadOnly = !impersonatedClientId;

    const ziele: Ziel[] = [
        { id: 'ziel1', text: 'Preisanpassung planen und umsetzen: Erarbeite eine Strategie, wie und wann die neuen Preise kommuniziert werden.' },
        { id: 'ziel2', text: 'Kalkulation der neuen Preise: Nutze die Daten aus dem Tool, um fundierte und profitable neue Preise festzulegen.' },
        { id: 'ziel3', text: 'Mitarbeitergespräche führen: Sprich offen mit deinem Team über die neuen Strukturen, Ziele und das Leistungslohnsystem.' },
        { id: 'ziel4', text: 'Leistungslohn einführen: Setze die Zusatzvereinbarung auf und integriere das neue Lohnsystem in deine Abläufe.' },
        { id: 'ziel5', text: 'Team-Meeting bei Deinem Betreuer buchen, damit dieser Deinem Team die Ziele, die Kalkulation, Leistungslohn und Preise erklären und gemeinsam mit ihnen planen kann.' },
        { id: 'ziel6', text: 'Lukrativität und Spezialisierung: Analysiere, welche Dienstleistungen am profitabelsten sind und wie du dein Angebot schärfen kannst. Wir unterstützen Dich dabei, lukrative Dienstleistungen in Deinem Unternehmen zu implementieren.' },
        { id: 'ziel7', text: 'Wirtschaftlichkeit und Controlling: Etabliere einen regelmäßigen Rhythmus, um deine Zahlen zu prüfen und den Kurs zu halten. Wir helfen Dir mit Hilfe unserer Partner, in lukrative und in Preis und Leistung attraktive Produkte zu investieren, ohne auf Qualität zu verzichten.' },
        { id: 'ziel8', text: (<span>Homepage überarbeiten: Aktualisiere deine Webseite mit den neuen Preisen und stelle sicher, dass sie deine Spezialisierung widerspiegelt. Gerne unterstützen wir Dich, eine professionelle auf die Beautybranche spezialisierte Website zu erstellen: Infos findest Du unter: <a href="https://www.friseur-coaching.de/webdesign/" target="_blank" rel="noopener noreferrer" className="text-pe-accent-text dark:text-[#E6C299] hover:underline font-semibold">https://www.friseur-coaching.de/webdesign/</a></span>) },
        { id: 'ziel9', text: 'Zusatzverkäufe trainieren: Schule dein Team gezielt darin, wie sie durch Upselling und Produktverkäufe ihre Provision steigern können.' },
        { id: 'ziel10', text: 'Kundenbindung verbessern: Entwickle Maßnahmen, um deine Stammkunden über die Änderungen zu informieren und sie weiterhin zu begeistern.' },
    ];

    const statusOptions: StatusOption[] = [
        { value: '', label: 'Priorität wählen...', color: 'bg-stone-200 text-stone-700 border border-stone-400 dark:bg-slate-700 dark:text-white dark:border-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]' },
        { value: 'dringend', label: 'Dringend', color: 'bg-red-500 text-white border border-red-300 shadow-[0_0_10px_rgba(239,68,68,0.18)]' },
        { value: 'wichtig', label: 'Wichtig', color: 'bg-yellow-500 text-black border border-yellow-300 shadow-[0_0_10px_rgba(234,179,8,0.16)]' },
        { value: 'optional', label: 'Optional', color: 'bg-blue-500 text-white border border-blue-300 shadow-[0_0_10px_rgba(59,130,246,0.16)]' },
        { value: 'erledigt', label: 'Erledigt', color: 'bg-green-500 text-white border border-green-300 shadow-[0_0_10px_rgba(34,197,94,0.16)]' },
        { value: 'nicht_umsetzen', label: 'Aktuell nicht umsetzen', color: 'bg-gray-500 text-white border border-gray-300 shadow-[0_0_10px_rgba(107,114,128,0.14)]' },
    ];

    const handleStatusChange = (zielId: string, newStatus: string) => {
        if (isReadOnly) return; // Doppelte Sicherheit
        setClientData(prev => ({
            ...prev,
            zieleStatus: {
                ...prev.zieleStatus,
                [zielId]: newStatus
            }
        }));
    };

    const handleNotizChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (isReadOnly) return; // Doppelte Sicherheit
        setClientData(prev => ({
            ...prev,
            zieleCoachNotiz: e.target.value
        }));
    };

    const getStatusColor = (zielId: string): string => {
        const status = clientData.zieleStatus?.[zielId];
        return statusOptions.find(opt => opt.value === status)?.color || 'bg-stone-200 text-stone-700 border border-stone-400 dark:bg-slate-700 dark:text-white dark:border-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]';
    };

    return (
        <Page>
            <DashboardStyles />

            <div className="ziele-carbon-shell bg-executive-night font-sans text-pe-text dark:text-slate-200 selection:bg-[#E6C299] selection:text-black p-4 sm:p-8">
                <div className="max-w-4xl mx-auto executive-surface p-6 lg:p-8">
                    <div className="content-layer">
                        <Section>
                            <PageTitle>
                                <span className="text-pe-text dark:text-white uppercase tracking-widest font-black flex items-center gap-3">
                                    Ziele und Fahrplan
                                    {isReadOnly && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-stone-200/80 dark:bg-slate-800/80 border border-stone-300 dark:border-slate-700 text-[10px] text-stone-600 dark:text-slate-300 normal-case tracking-normal font-bold">
                                            <Lock className="w-3 h-3 text-pe-accent-text dark:text-[#E6C299]" /> Wird vom Coach verwaltet
                                        </span>
                                    )}
                                </span>
                            </PageTitle>

                            <p className="text-pe-muted dark:text-slate-400 mb-8 -mt-4 leading-relaxed">
                                Die Kalkulation steht, alle Werte sind klar und die Preissituation wurde mit Euch besprochen – aber wie ist nun der Fahrplan?
                            </p>

                            <div className="ziele-module">
                                <div className="content-layer flex flex-col p-4 sm:p-5">
                                    {/* Header Row */}
                                    <div className="ziele-header-row flex gap-6 border-b-2 section-divider pb-3 mb-3 rounded-t-xl">
                                        <div className="w-1/3 flex-shrink-0">
                                            <h4 className="text-lg font-black text-center text-pe-text dark:text-white uppercase tracking-wide">
                                                Priorität
                                            </h4>
                                        </div>
                                        <div className="w-2/3">
                                            <h4 className="text-lg font-black text-pe-text dark:text-white uppercase tracking-wide">
                                                Aufgabe
                                            </h4>
                                        </div>
                                    </div>

                                    {/* Data Rows */}
                                    {ziele.map(ziel => (
                                        <div key={ziel.id} className="ziele-row flex gap-6 border-b border-pe-border dark:border-[#1f2a36] last:border-b-0">
                                            <div className="w-1/3 flex-shrink-0 flex items-center justify-center py-4">
                                                <select
                                                    value={clientData.zieleStatus?.[ziel.id] || ''}
                                                    onChange={(e) => handleStatusChange(ziel.id, e.target.value)}
                                                    disabled={isReadOnly}
                                                    className={`ziele-select w-full p-3 rounded-xl font-bold text-center appearance-none transition-all duration-200 focus:outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_6px_12px_rgba(0,0,0,0.20)] ${getStatusColor(ziel.id)} ${isReadOnly ? 'cursor-not-allowed opacity-90' : 'cursor-pointer hover:scale-[1.02]'}`}
                                                >
                                                    {statusOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="w-2/3 flex items-center py-4">
                                                <p className="text-pe-muted dark:text-slate-300 leading-relaxed">
                                                    {ziel.text}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-12">
                                <h4 className="text-xl font-black text-pe-text dark:text-white pt-4 border-t border-pe-border dark:border-[#283445] uppercase tracking-wide">
                                    Notizen vom Coach
                                </h4>
                                <textarea
                                    className={`w-full mt-3 p-4 border border-pe-border-strong dark:border-[#333] bg-pe-input dark:bg-black text-pe-text dark:text-white rounded-xl h-40 focus:outline-none shadow-sm dark:shadow-[inset_0_2px_8px_rgba(0,0,0,0.65),0_12px_26px_rgba(0,0,0,0.18)] placeholder:text-stone-400 dark:placeholder:text-slate-600 transition-colors ${isReadOnly ? 'cursor-default opacity-80' : 'focus:ring-2 focus:ring-[#E6C299]/50'}`}
                                    placeholder={isReadOnly ? "Keine aktuellen Notizen vorhanden." : "Hier kann der Coach persönliche Notizen und nächste Schritte für den Mandanten eintragen..."}
                                    value={clientData.zieleCoachNotiz || ''}
                                    onChange={handleNotizChange}
                                    readOnly={isReadOnly}
                                ></textarea>
                            </div>
                        </Section>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default ZielePage;